"use client"

import { ArrowRight, ShoppingBag, UserRound } from "lucide-react"
import Link from "next/link"

import { localizeWebAccountPath } from "@/lib/account-links"

import { accountText, type LocaleProps } from "../../personal-web"

/**
 * Bottom-of-page CTA banner on the public /installation-guide page.
 *
 * Two equal cards side-by-side covering both audiences:
 *   - "I already bought an eSIM" → /account/esim (login wall surfaces if
 *     not signed in)
 *   - "I haven't bought yet"     → / homepage (country picker)
 *
 * Mobile collapses to stacked. Card background uses a soft orange wash
 * so it reads as the visual conclusion of the page without screaming.
 */
export function GuideCTABanner({ lng }: LocaleProps) {
  const myEsimHref = localizeWebAccountPath("/account/esim", lng)
  const shopHref = `/${lng}`

  return (
    <section
      aria-label={accountText(lng, "install.guide.ctaSectionAriaLabel")}
      className="grid gap-3 md:grid-cols-2"
    >
      <Link
        href={myEsimHref}
        className="group flex items-center gap-4 rounded-2xl border border-orange-200 bg-orange-50/60 p-5 transition-colors hover:border-orange-300 hover:bg-orange-50"
      >
        <span
          aria-hidden
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-[0_6px_16px_-8px_rgba(234,88,12,0.7)]"
        >
          <UserRound className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-extrabold text-slate-950">
            {accountText(lng, "install.guide.ctaAlreadyBoughtTitle")}
          </p>
          <p className="mt-0.5 text-xs text-stone-600">
            {accountText(lng, "install.guide.ctaAlreadyBoughtBody")}
          </p>
        </div>
        <ArrowRight
          className="h-4 w-4 shrink-0 text-orange-600 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </Link>
      <Link
        href={shopHref}
        className="group flex items-center gap-4 rounded-2xl border border-stone-200 bg-white p-5 transition-colors hover:border-stone-300 hover:bg-stone-50"
      >
        <span
          aria-hidden
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-stone-900 text-white shadow-[0_6px_16px_-8px_rgba(28,25,23,0.5)]"
        >
          <ShoppingBag className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-extrabold text-slate-950">
            {accountText(lng, "install.guide.ctaShopTitle")}
          </p>
          <p className="mt-0.5 text-xs text-stone-600">
            {accountText(lng, "install.guide.ctaShopBody")}
          </p>
        </div>
        <ArrowRight
          className="h-4 w-4 shrink-0 text-stone-600 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </Link>
    </section>
  )
}
